/**
 * Procedural Quest Templates
 * Templates for generating random quests dynamically
 */

import {
  QuestType,
  QuestObjectiveType,
} from '@/types';

/**
 * Template for a procedural quest
 */
export interface ProceduralQuestTemplate {
  id: string;
  type: QuestType;
  category: 'daily' | 'challenge' | 'bounty' | 'buddy_quest';

  // Title and description templates
  // Use {species}, {count}, {quality}, etc. for placeholders
  nameTemplate: string;
  descriptionTemplate: string;

  // Objective configuration
  objectiveType: QuestObjectiveType;
  targetRange: [number, number]; // Min/max values

  // Filters (optional)
  useSpeciesFilter?: boolean;
  useQualityFilter?: boolean;
  useWeightFilter?: boolean;

  // Reward scaling
  baseReward: number; // Base currency reward
  rewardPerTarget: number; // Additional reward per target unit
}

/**
 * Catch Quantity Template - "Catch X fish"
 */
export const PROC_CATCH_QUANTITY: ProceduralQuestTemplate = {
  id: 'proc_catch_quantity',
  type: QuestType.CATCH_QUANTITY,
  category: 'daily',

  nameTemplate: "Daily Catch",
  descriptionTemplate: "Catch {count} fish today!",

  objectiveType: QuestObjectiveType.CATCH_FISH,
  targetRange: [5, 20],

  baseReward: 50,
  rewardPerTarget: 10,
};

/**
 * Catch Quality Template - "Catch X quality fish"
 */
export const PROC_CATCH_QUALITY: ProceduralQuestTemplate = {
  id: 'proc_catch_quality',
  type: QuestType.CATCH_QUALITY,
  category: 'challenge',

  nameTemplate: "Quality Catch",
  descriptionTemplate: "Catch {count} {quality} quality fish!",

  objectiveType: QuestObjectiveType.CATCH_FISH,
  targetRange: [3, 10],
  useQualityFilter: true,

  baseReward: 75,
  rewardPerTarget: 15,
};

/**
 * Catch Species Template - "Catch X of specific species"
 */
export const PROC_CATCH_SPECIES: ProceduralQuestTemplate = {
  id: 'proc_catch_species',
  type: QuestType.CATCH_SPECIES,
  category: 'bounty',

  nameTemplate: "{species} Bounty",
  descriptionTemplate: "Catch {count} {species} for a reward!",

  objectiveType: QuestObjectiveType.CATCH_FISH,
  targetRange: [3, 8],
  useSpeciesFilter: true,

  baseReward: 100,
  rewardPerTarget: 20,
};

/**
 * Sell Quantity Template - "Sell X fish"
 */
export const PROC_SELL_QUANTITY: ProceduralQuestTemplate = {
  id: 'proc_sell_quantity',
  type: QuestType.SELL_SPECIES,
  category: 'daily',

  nameTemplate: "Market Day",
  descriptionTemplate: "Sell {count} fish at the market!",

  objectiveType: QuestObjectiveType.SELL_FISH,
  targetRange: [5, 15],

  baseReward: 60,
  rewardPerTarget: 12,
};

/**
 * Earn Currency Template - "Earn X gold"
 */
export const PROC_EARN_CURRENCY: ProceduralQuestTemplate = {
  id: 'proc_earn_currency',
  type: QuestType.EARN_CURRENCY,
  category: 'challenge',

  nameTemplate: "Gold Rush",
  descriptionTemplate: "Earn {count} gold from fishing!",

  objectiveType: QuestObjectiveType.EARN_CURRENCY,
  targetRange: [200, 1000],

  baseReward: 50,
  rewardPerTarget: 0.2, // 20% bonus
};

/**
 * Buddy Catch Quantity - "Your buddies catch X fish"
 * Higher targets since buddies fish automatically
 */
export const PROC_BUDDY_CATCH_QUANTITY: ProceduralQuestTemplate = {
  id: 'proc_buddy_catch_quantity',
  type: QuestType.CATCH_QUANTITY,
  category: 'buddy_quest',

  nameTemplate: "Buddy Fishing Haul",
  descriptionTemplate: "Have your fishing buddies catch {count} fish!",

  objectiveType: QuestObjectiveType.BUDDY_CATCH_FISH,
  targetRange: [25, 100],

  baseReward: 150,
  rewardPerTarget: 8,
};

/**
 * Buddy Catch Species - "Your buddies catch X of a specific species"
 */
export const PROC_BUDDY_CATCH_SPECIES: ProceduralQuestTemplate = {
  id: 'proc_buddy_catch_species',
  type: QuestType.CATCH_SPECIES,
  category: 'buddy_quest',

  nameTemplate: "Buddy {species} Run",
  descriptionTemplate: "Have your fishing buddies catch {count} {species}!",

  objectiveType: QuestObjectiveType.BUDDY_CATCH_FISH,
  targetRange: [15, 50],
  useSpeciesFilter: true,

  baseReward: 200,
  rewardPerTarget: 12,
};

/**
 * Hauler Sell Quantity - "Your haulers sell X fish"
 */
export const PROC_HAULER_SELL_QUANTITY: ProceduralQuestTemplate = {
  id: 'proc_hauler_sell_quantity',
  type: QuestType.SELL_SPECIES,
  category: 'buddy_quest',

  nameTemplate: "Hauler Delivery",
  descriptionTemplate: "Have your haulers deliver and sell {count} fish at the market!",

  objectiveType: QuestObjectiveType.HAULER_SELL_FISH,
  targetRange: [20, 75],

  baseReward: 175,
  rewardPerTarget: 10,
};

/**
 * Hauler Earn Gold - "Your haulers earn X gold"
 */
export const PROC_HAULER_EARN_GOLD: ProceduralQuestTemplate = {
  id: 'proc_hauler_earn_gold',
  type: QuestType.EARN_CURRENCY,
  category: 'buddy_quest',

  nameTemplate: "Hauler Gold Rush",
  descriptionTemplate: "Have your haulers earn {count} gold from market sales!",

  objectiveType: QuestObjectiveType.HAULER_EARN_GOLD,
  targetRange: [500, 2000],

  baseReward: 200,
  rewardPerTarget: 0.15,
};

/**
 * All procedural quest templates
 */
export const PROCEDURAL_QUEST_TEMPLATES: ProceduralQuestTemplate[] = [
  PROC_CATCH_QUANTITY,
  PROC_CATCH_QUALITY,
  PROC_CATCH_SPECIES,
  PROC_SELL_QUANTITY,
  PROC_EARN_CURRENCY,
  PROC_BUDDY_CATCH_QUANTITY,
  PROC_BUDDY_CATCH_SPECIES,
  PROC_HAULER_SELL_QUANTITY,
  PROC_HAULER_EARN_GOLD,
];

/**
 * Get a template by ID
 */
export function getProceduralTemplate(templateId: string): ProceduralQuestTemplate | undefined {
  return PROCEDURAL_QUEST_TEMPLATES.find((t) => t.id === templateId);
}

/**
 * Get all templates of a specific category
 */
export function getTemplatesByCategory(
  category: 'daily' | 'challenge' | 'bounty' | 'buddy_quest'
): ProceduralQuestTemplate[] {
  return PROCEDURAL_QUEST_TEMPLATES.filter((t) => t.category === category);
}
